import * as Papa from 'papaparse';
import { noop } from 'vet-bones/utils';

import { formatSeparator } from 'src/utils/csv/formatSeparator';

type CsvField<T extends Record<string, any>> = {
  label: string;
  value: string;
  statistics: T;
};

export type ExtendedParseError = Partial<Papa.ParseError> &
  Pick<Papa.ParseError, 'message' | 'row'>;

export type FullFileStats = {
  records: number[];
  distinct: number[];
  totalRows: number;
};

export type ParseCsvResult<T extends Record<string, any>> = {
  data?: {
    rows: string[][];
    fields: Array<CsvField<T>>;
  };
  errors?: ExtendedParseError[];
  fullFileStats?: FullFileStats;
};

export const parseCsv = <T extends Record<string, any>>(
  file: string | File,
  options: Partial<Papa.ParseConfig>,
  defaultStatistics: T,
  assignStatistics?: (fields: Array<CsvField<T>>, rows: string[][]) => void,
  limitRows?: number
) => {
  let abort: () => void = noop;

  const fields: Array<CsvField<T>> = [];
  const rows: string[][] = [];
  const errors: ExtendedParseError[] = [];

  const statsTracking = {
    distinctSets: [] as Array<Set<string>>,
    recordCounts: [] as number[],
    totalRows: 0,
  };

  let resolveStats: ((stats: FullFileStats | undefined) => void) | null = null;
  let rejectStats: ((errors: ExtendedParseError[]) => void) | null = null;
  const statsPromise =
    limitRows != null
      ? new Promise<FullFileStats | undefined>((resolve, reject) => {
          resolveStats = resolve;
          rejectStats = reject;
        })
      : undefined;

  let previewResolved = false;

  const parsePromise = new Promise<ParseCsvResult<T>>((resolve) => {
    let parser: Papa.Parser | null = null;
    abort = () => {
      if (parser) {
        parser.abort();
        parser = null;
        if (!previewResolved) {
          resolve({ errors });
        }
        if (previewResolved && errors.length) {
          rejectStats?.(errors);
        } else {
          resolveStats?.(undefined);
        }
      }
    };

    const buildFullFileStats = (): FullFileStats => ({
      records: [...statsTracking.recordCounts],
      distinct: statsTracking.recordCounts.map((count, idx) =>
        count > 0 ? (statsTracking.distinctSets[idx].size / count) * 100 : 0
      ),
      totalRows: statsTracking.totalRows,
    });

    const complete = () => {
      if (assignStatistics && !previewResolved) {
        assignStatistics(fields, rows);
      }

      const fullFileStats = buildFullFileStats();

      parser = null;
      if (!previewResolved) {
        resolve({ data: { fields, rows }, fullFileStats });
      }
      resolveStats?.(fullFileStats);
    };

    let lastPause = Date.now();
    const handleStep: Papa.ParseConfig['step'] = async (result, stepParser) => {
      if (result.meta.aborted) {
        return;
      }

      parser = stepParser;

      const now = Date.now();
      if (now - lastPause > 200) {
        lastPause = now;

        parser.pause();
        setTimeout(() => {
          parser?.resume();
        }, 0);
      }

      if (result.errors.length) {
        errors.push(...result.errors);
        abort();
        return;
      }

      if (!fields.length) {
        if (result.meta.fields) {
          if (result.meta.fields.some((field) => !field)) {
            const headerPreview = result.meta.fields.join(',');
            errors.push({
              message: `Error importing: A header name is missing in [${headerPreview}]`,
              row: 0,
            });
            abort();
            return;
          }
          fields.push(
            ...result.meta.fields.map<CsvField<T>>((field) => ({
              label: field,
              value: field,
              statistics: { ...defaultStatistics },
            }))
          );
        } else {
          fields.push(
            ...new Array(result.data.length || 0)
              .fill('')
              .map<CsvField<T>>((_, idx) => ({
                label: `Field ${idx + 1}`,
                value: `${idx}`,
                statistics: { ...defaultStatistics },
              }))
          );
        }
      }

      // After the preview limit, we only need values for stats tracking.
      // Avoid fields.map() which allocates a throwaway array per row —
      // for a 14M-row file that's ~1GB of short-lived garbage and ~275
      // minor GC cycles.
      if (previewResolved) {
        statsTracking.totalRows += 1;
        fields.forEach((field, idx) => {
          const value = Array.isArray(result.data)
            ? result.data[idx]
            : (result.data as Record<string, string>)[field.value];
          if (!value) {
            return;
          }
          statsTracking.recordCounts[idx] += 1;
          statsTracking.distinctSets[idx].add(value);
        });
        return;
      }

      const row = Array.isArray(result.data)
        ? result.data
        : fields.map(
            (field) => (result.data as Record<string, string>)[field.value]
          );

      if (!statsTracking.recordCounts.length && row.length) {
        statsTracking.recordCounts = new Array(row.length).fill(0);
        statsTracking.distinctSets = row.map(() => new Set());
      }

      statsTracking.totalRows += 1;
      row.forEach((value, idx) => {
        // Without a header row papaparse hands back a raw array per line, so a
        // ragged file can widen past the first row. Cells beyond the columns we
        // display don't get columns in the preview table either — count only
        // what fits, like the post-preview branch above, instead of indexing
        // past the end of `distinctSets`.
        if (!value || idx >= statsTracking.recordCounts.length) {
          return;
        }
        statsTracking.recordCounts[idx] += 1;
        statsTracking.distinctSets[idx].add(value);
      });

      if (limitRows != null && rows.length >= limitRows) {
        previewResolved = true;
        if (assignStatistics) {
          assignStatistics(fields, rows);
        }
        resolve({ data: { fields, rows } });
        return;
      }

      rows.push(row);
    };

    const handleComplete: Papa.ParseConfig['complete'] = (result) => {
      if (result.meta.aborted) {
        return;
      }

      complete();
    };

    // papaparse can actually take File, I think they removed the
    // File type because nodejs can't handle File
    Papa.parse(file as any, {
      ...options,
      delimiter:
        typeof options.delimiter === 'string'
          ? formatSeparator(options.delimiter)
          : options.delimiter,
      skipEmptyLines: true,
      step: handleStep,
      complete: handleComplete,
    });
  });

  return {
    abort: () => {
      errors.length = 0;
      abort();
    },
    parsePromise,
    statsPromise,
  };
};
